/* eslint-disable no-underscore-dangle */
import Qs from 'qs';

import accessTokenService from 'utils/accessTokenService';
import {
  createAccessTokenRefresher,
  KINETIK_ACCESS_TOKEN_ENDPOINT,
} from 'utils/createAccessTokenRefresher';

import { APIAdapter } from '../adapter';

const DEFAULT_KINETIK_SERVICE_VERSION = 'v1.1';

/**
 * Resolves the per-version base URL for Kinetik service-API calls.
 *
 * `KINETIK_SERVICES_API_URL` currently ends with `/v1`. v1.1 is the legacy
 * services version and is the default — appending `.1` to the env var
 * produces `${host}/v1.1`, exactly as callers have always seen. v2
 * callers need a different terminal path segment (`/v2`), so we strip
 * any trailing `/v1` from the env var and append `/v2` instead.
 *
 * Resolver lookup is safe against future env-var shape changes: the v1.1
 * branch is unchanged (it just appends `.1` to whatever the env var is),
 * and the v2 branch's `replace` is a no-op when `/v1` is absent — so v2
 * still lands at `${host}/v2` if the env var ever drops the `/v1`
 * suffix.
 *
 * @param {string} version - Service-API version literal (e.g. `'v1.1'`, `'v2'`).
 * @returns {string} Fully resolved base URL for the requested version.
 */
const resolveKinetikServiceBaseURL = (version) => {
  const base = process.env.KINETIK_SERVICES_API_URL;
  if (version === 'v2') {
    return `${base.replace(/\/v1$/, '')}/v2`;
  }
  return `${base}.1`;
};

/**
 * Singleton axios instance for the Kinetik API.
 *
 * Callers opt into service-API version routing by setting `serviceName` on
 * the per-request axios config. `serviceVersion` selects the version
 * (defaults to `'v1.1'`). The request interceptor rewrites `baseURL` to
 * the resolved host; without `serviceName`, requests go straight to
 * `KINETIK_SERVICES_API_URL` (the legacy `/v1` path).
 *
 * Endpoint files should pin `serviceVersion` explicitly (e.g. `'v2'`) or
 * resolve it from a per-service helper (`get<Service>ServiceVersion()`)
 * for flag-gated rollouts.
 *
 * @type {import('axios').AxiosInstance}
 */
export const kinetikApi = APIAdapter(process.env.KINETIK_SERVICES_API_URL, {
  paramsSerializer: (params) => Qs.stringify(params, { arrayFormat: 'repeat' }),
});

// TODO: Consider using jwt-decode package
const parseJwt = (token) => {
  if (!token) return {};

  return JSON.parse(Buffer.from(token.split('.')[1], 'base64').toString());
};

const refreshAccessToken = createAccessTokenRefresher(kinetikApi);

// Request interceptor to route service-API calls and add Authorization header to every request
kinetikApi.interceptors.request.use(
  async (config) => {
    const newConfig = config;

    // Route service-API calls to the right version of the services host.
    // Callers signal "this is a service-API request" by setting
    // `serviceName`; `serviceVersion` picks the version (defaulting to
    // `'v1.1'` so legacy callers keep hitting `${url}.1` unchanged).
    if (config.serviceName) {
      newConfig.baseURL = resolveKinetikServiceBaseURL(
        config.serviceVersion ?? DEFAULT_KINETIK_SERVICE_VERSION,
      );
    }

    // Only add the Authorization header to requests that are not for a new access token
    if (config.url !== KINETIK_ACCESS_TOKEN_ENDPOINT) {
      let accessToken = accessTokenService.getAccessToken();

      // If the access token is missing or is expired, refresh it
      const { exp } = parseJwt(accessToken);
      if (!accessToken || Date.now() >= exp * 1000) {
        accessToken = await refreshAccessToken();
      }

      newConfig.headers.Authorization = `Bearer ${accessToken}`;
    }

    return newConfig;
  },
  (error) => {
    Promise.reject(error);
  },
);

// Response interceptor to retry unauthorized requests after refreshing the access token
kinetikApi.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    // If the request is NOT for a new access token,
    //  and the error is 401 (Unauthorized),
    //  and this is not a retry,
    // then refresh the access token and retry the request
    if (
      originalRequest.url !== KINETIK_ACCESS_TOKEN_ENDPOINT &&
      error.response?.status === 401 &&
      !originalRequest._retry
    ) {
      originalRequest._retry = true;

      // Attempt to refresh the access token
      const newAccessToken = await refreshAccessToken();

      // Update the original request with the new access token
      originalRequest.headers.Authorization = `Bearer ${newAccessToken}`;

      // Retry the original request
      return kinetikApi(originalRequest);
    }

    // An unhandled error occurred
    return Promise.reject(error);
  },
);
